// <copyright file="quote-state-change-helper.ts" company="SoftTech Ltd">
// Copyright (c) SoftTech Ltd. All rights reserved.
// </copyright>

import {
  AskConfirmation,
  ConfirmationButtonType,
  getConfirmationButtons
} from '../../../../webmodule-common/other/ui/modal-confirmation';
import { appOutOfDate } from '../../../../webmodule-common/other/debug';
import { checkSuppliersIsOnline } from '../quote-creator.js';
import { createConcurrencyCheck } from '../../common/resource-lock';
import { fireQuickSuccessToast } from '../../../../webmodule-common/other/toast-away';
import { FranchiseeQuoteContainerManager } from '../data/franchisee-quote-manager';
import { getApiFactory } from '../../../api/api-injector';
import { getCurrentUser } from '../../../../webmodule-common/other/api/current-user';
import { getQuoteContainer, getQuoteContainerManager, QuoteLock } from '../quote-ui-adapter';
import { goURL } from '../../../../webmodule-common/other/ui/resource-resolver';
import { information } from '../../../../webmodule-common/other/ui/modal-option';
import { LangFranchiseeQuoteListView } from '../../../../localization/scripts/dealer-franchisee/quotes/views/franchisee-quote-list-view-lang';
import { QuoteState } from '../../../api/dealer-api-interface-quote';
import { ResultGetSupplierQuoteSettings } from '../../../api/dealer-api-interface-all';
import { resolveAllowSupplierReviewRule } from '../capabilities/quote-row-capabilities';
import { resourceQuote } from '../../../quotes/ui/launcher';
import { router } from '../../../../webmodule-common/other/router';
import { userDataStore } from '../../common/current-user-data-store';

/** Outcome of the during-lock mutation. `void` covers actions (e.g. delete) that don't classify further. */
export type ActionResult = 'success' | 'validation-needed' | 'failed' | void;

/**
 * A single quote action modelled as three phases so the lock is never held across a user prompt and the
 * manager is never shared between rows:
 *  - preLockGate:  confirmations / choices, BEFORE any lock is taken (return false to abort).
 *  - duringLock:   the one place a mutation happens, against a manager freshly loaded UNDER the lock.
 *  - afterLock:    refresh / navigate — runs ONLY on success, and receives the duringLock result.
 */
export interface LockedQuoteAction {
  preLockGate?: () => Promise<boolean>;
  duringLock: (qcm: FranchiseeQuoteContainerManager) => Promise<ActionResult>;
  afterLock?: (result: ActionResult) => Promise<void>;
  /** State changes that may notify the supplier require it online; delete does NOT set this. */
  requiresSupplierOnline?: boolean;
}

/**
 * Runs quote ellipsis actions (cancel / accept / reject / delete) safely from a list, mirroring the
 * canonical app-quote flow: lock first, load under the lock, mutate, release. Everything that used to
 * be shared static state (the manager) is now a per-invocation local, so two rows / two tables cannot
 * race. Only the supplier-config cache is static — it is a genuine read-mostly cache keyed by supplier.
 */
export class QuoteStateActions {
  /** Read-mostly cache of supplier quote settings, keyed by supplierId. Cleared on user switch. */
  static supplierConfigCache: Map<string, ResultGetSupplierQuoteSettings> = new Map();

  /** Drop cached supplier config (call on logout / user switch so stale allow-review can't leak). */
  static clearSupplierConfigCache(): void {
    this.supplierConfigCache.clear();
  }

  /** Guard run before any action: bounce to reload if the app is stale, or to login if signed out. */
  static async checkBeforeRun(): Promise<boolean> {
    if (appOutOfDate()) {
      window.location.reload();
      return false;
    }
    if (getCurrentUser() === null) {
      router.go('/login');
      return false;
    }
    return true;
  }

  static async runLockedQuoteAction(id: string, supplierId: string, action: LockedQuoteAction): Promise<void> {
    // Phase 1 — confirmations happen BEFORE the lock, so we never hold a lock across a modal.
    if (action.preLockGate && !(await action.preLockGate())) {
      return;
    }

    // Only state changes that can notify the supplier gate on supplier-online. Delete never sets this,
    // so deleting a draft is not blocked by an offline supplier (a regression fixed from the prior PR).
    if (action.requiresSupplierOnline && supplierId && !(await checkSuppliersIsOnline(supplierId))) {
      await information(
        LangFranchiseeQuoteListView.supplierOfflineMessage(),
        LangFranchiseeQuoteListView.supplierOfflineTitle()
      );
      return;
    }

    // qcm is a LOCAL (never static): the concurrency check reads this invocation's manager only.
    let qcm: FranchiseeQuoteContainerManager | undefined;
    const concurrencyCheck = createConcurrencyCheck('QUOTE.QUOTE', id, async () => {
      await qcm?.needsQuote();
      return qcm?.quote?.recordVersion ?? '';
    });
    const lock = new QuoteLock(id, () => this.stateChangeFailed(id), concurrencyCheck);

    let succeeded = false;
    let result: ActionResult = undefined;
    try {
      const canUse = await lock.lock();
      if (!canUse || !lock.isLockOwner) {
        await information(
          LangFranchiseeQuoteListView.quoteLockedMessage({ ownerName: lock.lockOwnerName || 'another user' }),
          LangFranchiseeQuoteListView.quoteLockedTitle()
        );
        return;
      }

      // Phase 2 — load UNDER the lock (not before), then mutate. Result is CAPTURED, not discarded.
      qcm = getQuoteContainerManager(getQuoteContainer(id), lock) as FranchiseeQuoteContainerManager;
      await qcm.needsQuote();
      result = await action.duringLock(qcm);
      succeeded = result === 'success';
    } catch {
      await this.stateChangeFailed(id);
    } finally {
      await lock.release();
      if (qcm) qcm.lockInfo = undefined;
    }

    // Phase 3 — afterLock runs ONLY on success (never after a caught failure) and sees the result.
    if (succeeded && action.afterLock) {
      await action.afterLock(result);
    }
  }

  /**
   * Success toast for a STATE CHANGE (cancel / accept / reject). Delete has its own below — sharing
   * this one told the user their quote had been "updated" immediately after they deleted it.
   */
  static showSuccessMessage(): void {
    fireQuickSuccessToast(LangFranchiseeQuoteListView.quoteStateChanged());
  }

  static showDeletedMessage(): void {
    fireQuickSuccessToast(LangFranchiseeQuoteListView.quoteDeleted());
  }

  /**
   * Apply a state change under the lock. Accept and Approve first check item validation; if the quote
   * must be validated we inform the user (offering to open the quote) and return 'validation-needed'
   * rather than silently doing nothing — the validation path is wired, not dead.
   *
   * Approve is gated for the same reason Accept is, and the reason is not symmetry: with supplier
   * review off, Approve IS the row's accept, so gating only Accept would let exactly the tenants that
   * skip review push an unvalidated quote onward — a check the full quote screen has always applied to
   * both buttons.
   */
  static async applyStateChange(qcm: FranchiseeQuoteContainerManager, targetState: QuoteState): Promise<ActionResult> {
    const needsValidation = targetState === QuoteState.Accepted || targetState === QuoteState.Approved;
    if (needsValidation && (await qcm.currentUserMustValidate())) {
      await this.promptOpenQuote(qcm.quoteId);
      return 'validation-needed';
    }

    try {
      const ok = await qcm.quoteStateChange(targetState);
      if (ok) {
        this.showSuccessMessage();
        return 'success';
      }
      return 'failed';
    } catch {
      await this.stateChangeFailed(qcm.quoteId);
      return 'failed';
    }
  }

  /** Failure dialog that offers to open the quote so the change can be applied manually. */
  static async stateChangeFailed(quoteId: string): Promise<void> {
    await this.promptOpenQuote(quoteId);
  }

  private static async promptOpenQuote(quoteId: string): Promise<void> {
    const openQuote = await AskConfirmation(
      LangFranchiseeQuoteListView.failedProcessMessage(),
      getConfirmationButtons()[ConfirmationButtonType.okCancel]
    );
    if (openQuote) {
      goURL(resourceQuote, quoteId);
    }
  }

  /**
   * The already-resolved supplier-review gate for a supplier, folded into Accept by the capability
   * model — this is the single resolution point. The RULE itself lives in
   * `resolveAllowSupplierReviewRule` (pure, and unit-tested there, including the fabricator-mode
   * precedence from thread 85021); this method only supplies its two inputs.
   */
  static resolveAllowSupplierReview(supplierId: string): boolean {
    return resolveAllowSupplierReviewRule(
      userDataStore.isFabricatorMode,
      this.supplierConfigCache.get(supplierId)?.allowSupplierReview
    );
  }

  /** Load + cache a supplier's quote config (used to prefetch allowSupplierReview for the page's rows). */
  static async loadSupplierConfig(supplierId: string): Promise<void> {
    if (!supplierId || this.supplierConfigCache.has(supplierId)) {
      return;
    }
    try {
      const config = await getApiFactory().supplier().getSupplierQuoteConfig({ supplierId });
      if (config) {
        this.supplierConfigCache.set(supplierId, config);
      }
    } catch (error) {
      console.error(`Failed to load supplier config for ${supplierId}:`, error);
    }
  }

  /** Map an ellipsis menu value to its target state, or null for non-state actions (copy/delete). */
  static toTargetState(action: string): QuoteState | null {
    switch (action) {
      case 'accept':
        return QuoteState.Accepted;
      case 'approve':
        return QuoteState.Approved;
      case 'reject':
        return QuoteState.Rejected;
      case 'cancel':
        return QuoteState.Cancelled;
      default:
        return null;
    }
  }
}
